import { useId, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from "@mui/material";
import type { CognimeterLegendLayout } from "./CognimeterLegend";
import { TEAL } from "./Cognimeter";

const REFERENCE_ENTRIES = [
  { color: "Green", text: "Consistent with normal-performance reference cohort", background: "linear-gradient(180deg, rgb(30,160,72), rgb(64,174,64))", height: 68 },
  { color: "Gray", text: "Borderline / uncertainty zone (33–46)", background: "linear-gradient(180deg, #98a3b155, #98a3b1, #98a3b155)", height: 48 },
  { color: "Yellow", text: "Consistent with mild-impairment reference cohort", background: "linear-gradient(180deg, rgb(176,198,38), rgb(246,208,18) 45%, rgb(250,138,12))", height: 68 },
  { color: "Red", text: "Consistent with severe-impairment reference cohort", background: "linear-gradient(180deg, rgb(230,60,26), rgb(200,18,26))", height: 68 },
];

const REFERENCE_COMMENT = "Reference categories reflect MoCA/Qmci-based classifications in the Cognimeter development cohort and are shown only to provide context for score interpretation; they do not constitute clinical diagnoses.";

export default function CognimeterReferenceLegend({ layout }: { layout: CognimeterLegendLayout }) {
  const [commentOpen, setCommentOpen] = useState(false);
  const uid = useId();
  const titleId = `${uid}-reference-title`;
  const commentId = `${uid}-reference-comment`;
  const side = layout === "side";

  return (
    <Box
      component="aside"
      aria-labelledby={titleId}
      sx={{
        boxSizing: "border-box", width: side ? 190 : "100%", maxWidth: "100%", flex: "none",
        mt: side ? 0 : 2, p: 0.75, bgcolor: "#f5f8fc", color: "#26344a",
        border: "1px solid #dbe3ee", borderRadius: 1.5,
        // The arc's vertical center is 32/800 of its width below the SVG center.
        // A top margin of twice that offset shifts a flex-centered legend into place
        // while reserving space when the longer legend is taller than the gauge.
        // Gauge width = row width - 190px legend - 16px gap. Do not offset wrapped legends.
        ...(side && {
          "@container cognimeter (min-width: 626px)": { mt: "calc(8cqw - 16.48px)" },
        }),
      }}
    >
      <Typography id={titleId} component="h3" sx={{ m: 0, mb: 0.75, fontSize: 11.5, lineHeight: 1.25, fontWeight: 700, textAlign: "center" }}>
        SCORE REFERENCE AREAS
      </Typography>
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
        {REFERENCE_ENTRIES.map((entry, index) => (
          <Box
            component="li"
            key={entry.color}
            sx={{ display: "grid", gridTemplateColumns: "12px minmax(0, 1fr)", columnGap: 0.75, minHeight: side ? entry.height : 36 }}
          >
            <Box aria-hidden="true" sx={{ background: entry.background, borderRadius: index === 0 ? "3px 3px 0 0" : index === 3 ? "0 0 3px 3px" : 0 }} />
            <Box component="span" sx={{ alignSelf: "center", py: 0.25, fontSize: 12, lineHeight: 1.3, overflowWrap: "break-word" }}>
              <b>{entry.color}</b> — {entry.text}
            </Box>
          </Box>
        ))}
      </Box>
      <Button
        size="small"
        aria-haspopup="dialog"
        aria-expanded={commentOpen}
        onClick={() => setCommentOpen(true)}
        sx={{ display: "flex", mx: "auto", mt: 0.5, p: 0, minWidth: 0, minHeight: 28, color: TEAL, fontSize: 12, textTransform: "none", textDecoration: "underline" }}
      >
        Additional comment
      </Button>
      <Dialog open={commentOpen} onClose={() => setCommentOpen(false)} aria-labelledby={`${commentId}-title`} aria-describedby={commentId} maxWidth="sm" fullWidth>
        <DialogTitle id={`${commentId}-title`}>Additional comment</DialogTitle>
        <DialogContent>
          <DialogContentText id={commentId} sx={{ color: "#26344a" }}>{REFERENCE_COMMENT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
